import { createMemoryDriver, probe } from './driver'
import { KEYS, PORTAL_TOKENS, SCOPES, SESSION_KEYS, tokenKeyFor } from './keys'

export { KEYS, SESSION_KEYS, SCOPES, PORTAL_TOKENS, tokenKeyFor }

/**
 * Normalise a registry descriptor or a raw string into a key name.
 * @param {object|string} key - A KEYS entry, or a raw key string.
 * @returns {string|null} The key name, or null if unusable.
 */
function nameOf(key) {
  if (typeof key === 'string') {
    return key
  }
  return key && key.key ? key.key : null
}

/**
 * Build a storage instance over the first usable driver in `chain`.
 * The chain is probed ONCE at module load — the property access is what throws, so
 * per-call try/catch would cost more than it buys. Writes stay guarded anyway because
 * quota can fail long after a successful probe.
 * @param {Array<'localStorage'|'sessionStorage'>} chain - Areas to try, in order.
 * @param {object} registry - The key registry this instance governs; clearScope and
 *   clearPortal only ever touch keys declared in it.
 * @returns {object} The storage instance.
 */
function createStore(chain, registry) {
  const registeredKeys = Object.values(registry)
  let driver = null
  for (const kind of chain) {
    driver = probe(kind)
    if (driver) {
      break
    }
  }
  if (!driver) {
    driver = createMemoryDriver()
  }

  const store = {
    /** Name of the backing driver: 'localStorage' | 'sessionStorage' | 'memory'. */
    driverName: driver.name,

    /** False when values will NOT survive a reload (degraded mode). */
    isDurable: driver.durable,

    /**
     * Read a value.
     * @param {object|string} key - A KEYS entry or raw key string.
     * @returns {string|null} The stored string, or null when absent or unreadable.
     */
    get(key) {
      const name = nameOf(key)
      if (!name) {
        return null
      }
      try {
        return driver.get(name)
      } catch (e) {
        return null
      }
    },

    /**
     * Write a value. Silently no-ops if the write fails (quota, private mode).
     * @param {object|string} key - A KEYS entry or raw key string.
     * @param {*} value - Coerced to a string, matching Web Storage semantics.
     */
    set(key, value) {
      const name = nameOf(key)
      if (!name) {
        return
      }
      try {
        driver.set(name, String(value))
      } catch (e) {
        // Storage full or refused mid-session — dropping the write is preferable to
        // throwing from whatever lifecycle method we were called in.
      }
    },

    /**
     * Remove a single key.
     * @param {object|string} key - A KEYS entry or raw key string.
     */
    remove(key) {
      const name = nameOf(key)
      if (!name) {
        return
      }
      try {
        driver.remove(name)
      } catch (e) {
        // Nothing useful to do — the value is already unreachable.
      }
    },

    /**
     * Read and JSON-parse a value.
     * @param {object|string} key - A KEYS entry or raw key string.
     * @param {*} fallback - Returned when the value is absent or malformed.
     * @returns {*} The parsed value, or `fallback`.
     */
    getJSON(key, fallback = null) {
      const raw = store.get(key)
      if (raw === null || raw === undefined) {
        return fallback
      }
      try {
        return JSON.parse(raw)
      } catch (e) {
        return fallback
      }
    },

    /**
     * JSON-serialise and write a value.
     * @param {object|string} key - A KEYS entry or raw key string.
     * @param {*} value - Any JSON-serialisable value.
     */
    setJSON(key, value) {
      try {
        store.set(key, JSON.stringify(value))
      } catch (e) {
        // Value was not serialisable (circular ref) — drop the write rather than throw.
      }
    },

    /**
     * Remove every registered key in a scope, leaving unrelated keys alone.
     * @param {string} scope - A SCOPES value.
     */
    clearScope(scope) {
      registeredKeys.filter(entry => entry.scope === scope).forEach(entry => store.remove(entry))
    },

    /**
     * Remove every registered key belonging to one portal, so logging out of one
     * portal cannot take down another portal's session in the same browser.
     * @param {string} portal - Portal name, e.g. 'scout'.
     */
    clearPortal(portal) {
      registeredKeys.filter(entry => entry.portal === portal).forEach(entry => store.remove(entry))
    },

    /**
     * Wipe the whole storage area, including keys this app did not write.
     * Prefer clearScope/clearPortal — this is the blunt instrument.
     */
    clearAll() {
      try {
        driver.clear()
      } catch (e) {
        // Storage became unreadable mid-session; there is nothing left to clear.
      }
    },

    /**
     * Observe cross-tab writes to a key. No-ops (returns a no-op unsubscribe) when
     * the backing driver is not a real Web Storage area, since no events will fire.
     * @param {object|string} key - A KEYS entry or raw key string.
     * @param {Function} callback - Called with the StorageEvent.
     * @returns {Function} Unsubscribe function.
     */
    subscribe(key, callback) {
      const name = nameOf(key)
      if (!name || driver.name === 'memory' || typeof window === 'undefined') {
        return () => {}
      }
      const handler = event => {
        if (event.key === name) {
          callback(event)
        }
      }
      window.addEventListener('storage', handler)
      return () => window.removeEventListener('storage', handler)
    },
  }

  return store
}

/** Durable, cross-tab storage. Falls back to sessionStorage, then memory. */
export const storage = createStore(['localStorage', 'sessionStorage'], KEYS)

/** Per-tab storage. Falls back to memory. */
export const sessionStore = createStore(['sessionStorage'], SESSION_KEYS)

export default storage
